// This is a practitioner's profile component, as seen by an admin.
// a similar profile view to be seen / edited by the practitioner himself
// is different sufficiently to be implemented as a different component,
// although reusing some blocks from here.

import { type FunctionComponent, useState } from 'react';
import { Route, Routes, useNavigate } from 'react-router';

import { type OptionsT, newAsyncDataEnvelope } from '@dr.pogodin/react-utils';

import Throbber from 'components/Throbber';
import { ArtistMediaManager } from 'components/admin';
import { ProfileMenu } from 'components/admin/ProfileMenu';
import RemovePractitionerModal from 'components/admin/RemovePractitionerModal';

import useSetArtist from 'hooks/useSetArtist';

import AdminPractitionerIntroVideo from 'pages/admin/PractitionerIntroVideo';

import { Invoicing } from 'pages/admin/Profile/Invoicing';

import Modalities from 'pages/admin/Profile/Modalities';

import { deleteArtist, toggleArtistStatus, updateArtist } from 'services/artists';
import { setAvatar, updateUser as updateUserService } from 'services/user';

import {
  type ArtistStatusT,
  type ArtistT,
  E_INTRO_VIDEO,
  E_INVOICING,
  E_MEDIA,
  E_MODALITIES,
  P_ADMIN_PRACTITIONERS,
  P_WEBSITE_PRACTITIONERS,
  type UserT,
  getConfig,
  getId,
  useGlobalStateObject,
} from 'utils';

import throbberTheme from 'themes/AppThrobber/black.scss';

import AdminUserProfileData from '../UserProfileData';
import { UserProfileHeader } from '../UserProfileHeader';

import './style.scss';

const ARTIST_STATUS_OPTIONS: OptionsT<string> = [
  'Active' satisfies ArtistStatusT,
  'Inactive' satisfies ArtistStatusT,
  {
    name: 'Delete Practitioner',
    value: 'Deleted' satisfies ArtistStatusT,
  },
];

type PropsT = {
  // TODO: Hmm... these two being passed separately from a parent component,
  // while both originate from the same useArtist() call there, is not very
  // clean, to be refactored later.
  practitioner: ArtistT;
};

const CmsPractitionerProfile: FunctionComponent<PropsT> = ({
  practitioner,
}) => {
  const [busy, setBusy] = useState(false);

  const [showRemoveModal, setShowRemoveModal] = useState(false);

  const setArtist = useSetArtist();

  const gs = useGlobalStateObject();
  const navigate = useNavigate();

  const config = getConfig(true);

  const practitionerId = getId(practitioner);
  const baseUrl = `${P_ADMIN_PRACTITIONERS}/${practitionerId}`;
  const publicUrl = `${config.HTTP_SCHEMA}${config.DOMAIN}${P_WEBSITE_PRACTITIONERS}/${practitionerId}`;

  return (
    <div styleName="container">
      <UserProfileHeader
        displayName={practitioner.displayName}
        setStatus={(value) => {
          void (async () => {
            try {
              if (value === 'Deleted' satisfies ArtistStatusT) {
                setShowRemoveModal(true);
                return;
              }
              setBusy(true);
              setArtist({
                ...practitioner,
                status: value as ArtistStatusT,
              });
              const res = await updateArtist(getId(practitioner), {
                status: value as 'Active' | 'Inactive' satisfies ArtistStatusT,
              });
              setArtist(res);
            } catch {
              // TODO: We need an in-app error modal!
              // eslint-disable-next-line no-alert
              alert('Failed to change the status');
              setArtist(practitioner);
            } finally {
              setBusy(false);
            }
          })();
        }}
        status={practitioner.status}
        statusOptions={ARTIST_STATUS_OPTIONS}
        user={practitioner.user}
      />
      <div styleName="mainContentBlock">
        <ProfileMenu
          baseUrl={baseUrl}
          isLive={practitioner.published === true}
          onToggleLive={practitionerId ? () => {
            void (async () => {
              try {
                setBusy(true);
                const res = await toggleArtistStatus(practitionerId);
                setArtist(res);
              } catch {
                // eslint-disable-next-line no-alert
                alert('Failed to toggle status');
              } finally {
                setBusy(false);
              }
            })();
          } : undefined}
          publicUrl={publicUrl}
        />
        <Routes>
          <Route
            element={(
              <AdminUserProfileData
                editable
                introVideoUrl={`${baseUrl}${E_INTRO_VIDEO}`}
                key={practitioner.id}
                practitioner={practitioner}
                updateUser={async (update) => {
                  try {
                    const userId = getId(practitioner.user);
                    if (update.newAvatar) {
                      await setAvatar(userId, update.newAvatar);
                      setArtist({
                        ...practitioner,
                        user: {
                          ...practitioner.user,
                          avatarUrl: URL.createObjectURL(update.newAvatar),
                        },
                      });
                    }
                    if (update.user) {
                      await updateUserService(userId, update.user);
                    }
                    if (update.practitioner) {
                      const res = await updateArtist(
                        getId(practitioner),
                        update.practitioner,
                      );
                      setArtist(res);
                    }
                  } catch {
                    // eslint-disable-next-line no-alert
                    alert('Failed to save the changes');
                  }
                }}
                user={practitioner.user}
              />
            )}
            path=""
          />
          <Route
            element={(
              <Modalities
                key={practitioner.id}
                onUpdate={async (update) => {
                  try {
                    const userId = getId(practitioner.user);
                    if (update.newAvatar) {
                      await setAvatar(userId, update.newAvatar);
                      setArtist({
                        ...practitioner,
                        user: {
                          ...practitioner.user,
                          avatarUrl: URL.createObjectURL(update.newAvatar),
                        },
                      });
                    }
                    if (update.practitioner) {
                      const {
                        user: practitionerUserUpdate,
                        ...practitionerUpdate
                      } = update.practitioner;
                      if (practitionerUserUpdate) {
                        const res = await updateUserService(
                          userId,
                          practitionerUserUpdate,
                        );

                        let au = gs.get('adminUsers.data');
                        if (au) {
                          const idx = au.findIndex(
                            (item) => item.id === userId,
                          );
                          if (idx >= 0) {
                            au = [...au];
                            au[idx] = res;
                            gs.set('adminUsers.data', au);
                          }
                        }
                      }
                      if (Object.keys(practitionerUpdate).length > 0) {
                        const res = await updateArtist(
                          getId(practitioner),
                          practitionerUpdate,
                        );
                        setArtist(res);
                      }
                    }
                  } catch {
                    // eslint-disable-next-line no-alert
                    alert('Failed to save the changes');
                  }
                }}
                practitioner={practitioner}
                user={practitioner.user as unknown as UserT}
              />
            )}
            path={E_MODALITIES}
          />
          <Route
            element={(
              <Invoicing
                editable
                onUpdate={async (update) => {
                  try {
                    const userId = getId(practitioner.user);
                    const res = await updateUserService(userId, update);

                    let au = gs.get('adminUsers.data');
                    if (au) {
                      const idx = au.findIndex((item) => item.id === userId);
                      if (idx >= 0) {
                        au = [...au];
                        au[idx] = res;
                        gs.set('adminUsers.data', au);
                      }
                    }
                  } catch {
                    // eslint-disable-next-line no-alert
                    alert('Failed to save the changes');
                  }
                }}
                paymentMethod={practitioner.user.paymentMethod}
              />
            )}
            path={E_INVOICING}
          />
          <Route
            element={
              <ArtistMediaManager artist={practitioner} key={practitioner.id} />
            }
            path={E_MEDIA}
          />
          <Route
            element={(
              <AdminPractitionerIntroVideo
                practitionerId={practitioner.id}
              />
            )}
            path={E_INTRO_VIDEO}
          />
        </Routes>
      </div>
      {busy ? (
        <Throbber
          theme={throbberTheme}
          withDebounce
          withOverlay
        />
      ) : null}
      {showRemoveModal ? (
        <RemovePractitionerModal
          onCancel={() => {
            setShowRemoveModal(false);
          }}
          onRemove={async () => {
            const id = getId(practitioner);

            await deleteArtist(id);

            const list = {
              ...gs.get('artists.list'),
              operationId: '',
            };

            if (list.data) {
              const items: ArtistT[] = [];
              for (const item of list.data.items) {
                if (item.id !== id) items.push(item);
              }
              list.data = { ...list.data, items };
            }

            gs.set('artists.list', list);
            gs.set(`artists.map.${id}`, newAsyncDataEnvelope<ArtistT>());
            await navigate(P_ADMIN_PRACTITIONERS);
          }}
        />
      ) : null}
    </div>
  );
};

export default CmsPractitionerProfile;
